import React, { useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  makeStyles,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Input,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { useApp } from "../../context";
import { useTranslation } from "react-i18next";
import { Icon } from "@opendash/icons";
import { AudioMetadata } from "../DataDonationRoute";

interface Audio {
  url: string;
  name: string;
  audioBlob: Blob | File;
  metadata?: AudioMetadata;
}

interface MetadataErrors {
  confirmedRights?: string;
  confirmedDataset?: string;
  confirmedGame?: string;
}

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  audioPreview: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  audioControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  metadataForm: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  formField: {
    flex: 1,
    minWidth: 200,
  },
}));

type UploadCardProps = {
  audio: Audio;
  audioKey: string;
  onUpload: (
    audioKey: string,
    audio: Audio,
    metadata: AudioMetadata,
  ) => Promise<boolean>;
  onDelete?: (audioKey: string) => void;
};

export const UploadCard = observer(function UploadCard(props: UploadCardProps) {
  const app = useApp();
  const classes = useStyles();
  const { t } = useTranslation();

  // Audio playback states
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  // Metadata states
  const [metadata, setMetadata] = useState<AudioMetadata>({
    synthetic: false,
    voiceGender: "",
    speakerAge: "",
    spokenText: "",
    language: "",
    microphoneType: "",
    recordingEnvironment: "",
    confirmedRights: false,
    confirmedDataset: false,
    confirmedGame: false,
    ...props.audio.metadata, // Use any existing metadata
  });
  const [metadataErrors, setMetadataErrors] = useState<MetadataErrors>({});

  // Audio ref
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const validateMetadata = (): boolean => {
    const errors: MetadataErrors = {};

    setMetadataErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleMetadataChange = (
    field: keyof AudioMetadata,
    value: string | number | boolean,
  ) => {
    setMetadata((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if ((metadataErrors as any)[field]) {
      setMetadataErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleUpload = async () => {
    if (!validateMetadata()) {
      app.notifications.error(t("error-complete-metadata"));
      return;
    }

    setIsUploading(true);

    try {
      if (props.onUpload) {
        const success = await props.onUpload(
          props.audioKey,
          props.audio,
          metadata,
        );
        if (success) {
          setIsUploaded(true);
          app.notifications.success(
            "Audio erfolgreich gespendet! Vielen Dank für Ihren Beitrag.",
          );
        }
      } else {
        console.error("No onUpload handler provided.");
        app.notifications.error("Fehler beim Hochladen der Audio-Datei.");
      }
    } catch (error) {
      console.error("Error uploading audio donation:", error);
      app.notifications.error("Fehler beim Hochladen der Audio-Datei.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = () => {
    if (props.onDelete) {
      props.onDelete(props.audioKey);
    }
  };

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={2}
        >
          <Typography variant="h6">
            {t("audio-preview")} - {props.audioKey} -{" "}
            {props.audioKey.includes("recording-") ? (
              <Icon icon="fa:microphone" />
            ) : (
              <Icon icon="fa:file-audio" />
            )}
          </Typography>

          {props.onDelete && (
            <IconButton
              onClick={handleDelete}
              disabled={isUploading}
              size="small"
              color="secondary"
            >
              <Icon icon="fa:trash" />
            </IconButton>
          )}
        </Box>

        {/* Audio Preview */}
        <audio
          ref={audioRef}
          src={props.audio.url}
          className={classes.audioPreview}
          onEnded={() => setIsPlaying(false)}
          style={{ display: "none" }}
        />

        <div className={classes.audioControls}>
          <IconButton
            onClick={isPlaying ? pauseAudio : playAudio}
            color="primary"
          >
            {isPlaying ? <Icon icon="fa:pause" /> : <Icon icon="fa:play" />}
          </IconButton>

          <Typography variant="body2">
            {isPlaying ? t("audio-playing") : t("audio-ready")}
          </Typography>
        </div>

        {/* Metadata Form */}
        <Divider style={{ margin: "24px 0" }} />
        <Typography variant="h6" gutterBottom>
          {t("audio-metadata")}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {t("metadata-description")}
        </Typography>

        <div className={classes.metadataForm}>
          {/* Voice Synthetic and Gender and Age */}
          <div className={classes.formRow}>
            <FormControlLabel
              className={classes.formField}
              control={
                <Checkbox
                  checked={metadata.synthetic}
                  onChange={(e) =>
                    handleMetadataChange("synthetic", e.target.checked)
                  }
                  color="primary"
                />
              }
              label={t("voice-synthetic-bool")}
            />
            <FormControl className={classes.formField}>
              <InputLabel>{t("voice-gender")}</InputLabel>
              <Select
                value={metadata.voiceGender}
                onChange={(e) =>
                  handleMetadataChange("voiceGender", e.target.value as string)
                }
              >
                <MenuItem value="male">{t("voice-gender-male")}</MenuItem>
                <MenuItem value="female">{t("voice-gender-female")}</MenuItem>
                <MenuItem value="diverse">{t("voice-gender-diverse")}</MenuItem>
              </Select>
            </FormControl>{" "}
            <TextField
              className={classes.formField}
              label={t("speaker-age")}
              type="number"
              value={metadata.speakerAge}
              onChange={(e) =>
                handleMetadataChange(
                  "speakerAge",
                  parseInt(e.target.value) || "",
                )
              }
              inputProps={{ min: 1, max: 120 }}
            />
          </div>

          {/* Language */}
          <FormControl className={classes.formField}>
            <InputLabel>{t("language")}</InputLabel>
            <Select
              value={metadata.language}
              onChange={(e) =>
                handleMetadataChange("language", e.target.value as string)
              }
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="de">Deutsch</MenuItem>
              <MenuItem value="fr">Français</MenuItem>
              <MenuItem value="es">Español</MenuItem>
              <MenuItem value="it">Italiano</MenuItem>
              <MenuItem value="pt">Português</MenuItem>
              <MenuItem value="zh">中文</MenuItem>
              <MenuItem value="ja">日本語</MenuItem>
              <MenuItem value="ru">Русский</MenuItem>
              <MenuItem value="ar">العربية</MenuItem>
              <MenuItem value="hi">हिन्दी</MenuItem>
              <MenuItem value="bn">বাংলা</MenuItem>
              <MenuItem value="pa">ਪੰਜਾਬੀ</MenuItem>
              <MenuItem value="jv">Basa Jawa</MenuItem>
              <MenuItem value="ms">Bahasa Melayu</MenuItem>
              <MenuItem value="sw">Kiswahili</MenuItem>
              <MenuItem value="ta">தமிழ்</MenuItem>
              <MenuItem value="te">తెలుగు</MenuItem>
              <MenuItem value="vi">Tiếng Việt</MenuItem>
            </Select>
          </FormControl>

          {/* Spoken Text */}
          <TextField
            fullWidth
            label={t("text-spoken")}
            multiline
            value={metadata.spokenText}
            onChange={(e) => handleMetadataChange("spokenText", e.target.value)}
          />

          {/* Microphone Type and Recording Environment */}
          <div className={classes.formRow}>
            <FormControl
              className={classes.formField}
              disabled={metadata.synthetic}
            >
              <InputLabel>{t("microphone-type")}</InputLabel>
              <Select
                value={metadata.microphoneType}
                onChange={(e) =>
                  handleMetadataChange(
                    "microphoneType",
                    e.target.value as string,
                  )
                }
              >
                <MenuItem value="laptop">
                  {t("microphone-type-laptop")}
                </MenuItem>
                <MenuItem value="phone">{t("microphone-type-phone")}</MenuItem>
                <MenuItem value="wireless_headphone">
                  {t("microphone-type-wireless-headphone")}
                </MenuItem>
                <MenuItem value="professional">
                  {t("microphone-type-professional")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl
              className={classes.formField}
              disabled={metadata.synthetic}
            >
              <InputLabel>{t("recording-environment")}</InputLabel>
              <Select
                value={metadata.recordingEnvironment}
                onChange={(e) =>
                  handleMetadataChange(
                    "recordingEnvironment",
                    e.target.value as string,
                  )
                }
              >
                <MenuItem value="quiet">
                  {t("recording-environment-quiet")}
                </MenuItem>
                <MenuItem value="moderate_noise">
                  {t("recording-environment-moderate")}
                </MenuItem>
                <MenuItem value="noisy">
                  {t("recording-environment-noisy")}
                </MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        <Box
          display="flex"
          justifyContent="flex-end"
          style={{ gap: 16 }}
          marginTop={2}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={isUploading || isUploaded}
            startIcon={isUploading ? <CircularProgress size={20} /> : undefined}
          >
            {isUploaded
              ? "✓ Uploaded"
              : isUploading
              ? t("uploading")
              : t("donate-audio")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
});
